// src/hooks/useGlowAnchoredTo.js
import * as React from 'react';

// Measures from the lamp to the target's bottom and exposes:
//   --glow-offset-y    (vertical offset to place glow under the lamp)
//   --glow-height-used (height so the cone reaches the target)
export function useGlowAnchoredTo(targetSelector, opts = {}) {
  const { extra = 16, min = 260, max = 1100 } = opts;

  React.useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const root = document.documentElement;
    let retryCount = 0;
    const maxRetries = 20; // Try for up to 2 seconds
    
    const trySetup = () => {
      const target = document.querySelector(targetSelector);
      const host = document.querySelector('.glow-host');
      const lamp = document.querySelector('.porchlight-lamp-container');
      
      // Debug logging for element detection
      if (localStorage.getItem('DEBUG_GLOW') === 'true') {
        console.log(`Glow elements (attempt ${retryCount + 1}):`, { 
          target: !!target, 
          host: !!host, 
          lamp: !!lamp,
          targetSelector 
        });
      }
      
      if (!target || !host || !lamp) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(trySetup, 100);
        }
        return;
      }
      
      setupGlow(target, host, lamp);
    };
    
    const setupGlow = (target, host, lamp) => {
      let raf = 0;
      const measureNow = () => {
        raf = 0;

        const scrollY = window.scrollY || window.pageYOffset || 0;
        const hostTop = host.getBoundingClientRect().top + scrollY;
        const lampTop = lamp.getBoundingClientRect().top + scrollY;

        // place the glow relative to the lamp inside the host
        root.style.setProperty('--glow-offset-y', `${Math.max(0, lampTop - hostTop)}px`);

        // height from lamp top to bottom of the target, with a little extra for feather
        const targetBottom = target.getBoundingClientRect().bottom + scrollY;
        let h = Math.round(targetBottom - lampTop + extra);
        if (!Number.isFinite(h)) return;

        h = Math.max(min, Math.min(max, h));
        root.style.setProperty('--glow-height-used', `${h}px`);
      };

      const schedule = () => { if (!raf) raf = requestAnimationFrame(measureNow); };

      const ro = new ResizeObserver(schedule);
      ro.observe(document.body);
      ro.observe(target);
      ro.observe(lamp);
      ro.observe(host);

      const mo = new MutationObserver(schedule);
      mo.observe(target, { childList: true, subtree: true });

      window.addEventListener('resize', schedule);
      window.addEventListener('orientationchange', schedule);

      schedule(); // initial measure

      return () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
        mo.disconnect();
        window.removeEventListener('resize', schedule);
        window.removeEventListener('orientationchange', schedule);
      };
    };
    
    trySetup();
  }, [targetSelector, extra, min, max]);
}
